﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useContext } from "react";
import { useTextDomainContext } from "../../app/providers";
import type { MutationRemoveUserFromPartnerArgs } from "@/types/graphql";

/**
 * Custom hook to delete a user company.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{removeUserFromPartner: Function, deleting: boolean}} - The function to unlink a user from partner and the loading state.
 */
export function useRemoveUserFromPartner(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [removeUserFromPartner, { loading }] = useMutation<
    any,
    MutationRemoveUserFromPartnerArgs
  >(mutations.removeUserFromPartner, {
    onCompleted: (response) => {
      if (response?.removeUserFromPartner?.success) {
        enqueueSnackbar(gettext("The user has been unlinked."), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(gettext("Failed to unlink. ") + response.message, {
          variant: "error",
        });
      }
      onCompleted(response);
    },
    onError: (err) => {
      enqueueSnackbar("Error while unlink the user: " + err.message, {
        variant: "error",
      });
      onError(err);
    },
    errorPolicy: "all",
    refetchQueries: ["PartnersUsersList", "GetPartners"],
  });

  const removeUserFromPartnerRow = useCallback(
    (variables: MutationRemoveUserFromPartnerArgs) => {
      removeUserFromPartner({
        variables,
      }).then();
    },
    [removeUserFromPartner],
  );

  return {
    removeUserFromPartner: removeUserFromPartnerRow,
    deleting: loading,
  };
}
